import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Mail,
  Eye,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Send,
  MailOpen,
  ExternalLink,
} from "lucide-react";
import { format } from "date-fns";
import { getEmailTransactionsByClient, retryEmail, getEmailTransactionById } from "../data";

const STATUS_CONFIG = {
  PENDING: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  QUEUED: { label: "Queued", color: "bg-blue-100 text-blue-800", icon: Clock },
  SENT: { label: "Sent", color: "bg-green-100 text-green-800", icon: Send },
  DELIVERED: { label: "Delivered", color: "bg-green-100 text-green-800", icon: CheckCircle },
  OPENED: { label: "Opened", color: "bg-purple-100 text-purple-800", icon: MailOpen },
  FAILED: { label: "Failed", color: "bg-red-100 text-red-800", icon: XCircle },
  BOUNCED: { label: "Bounced", color: "bg-red-100 text-red-800", icon: AlertCircle },
};

const EMAIL_TYPE_CONFIG = {
  INVOICE_REQUEST: { label: "Invoice Request", color: "bg-blue-100 text-blue-800" },
  REMINDER: { label: "Reminder", color: "bg-orange-100 text-orange-800" },
  CHANGE_REQUEST: { label: "Change Request", color: "bg-yellow-100 text-yellow-800" },
  CONFIRMATION: { label: "Confirmation", color: "bg-green-100 text-green-800" },
  CUSTOM: { label: "Custom", color: "bg-muted text-foreground" },
};

export function EmailTransactionsTab({ clientId, type = "client" }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [emailTypeFilter, setEmailTypeFilter] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["emailTransactions", type, clientId, page, statusFilter, emailTypeFilter],
    queryFn: () =>
      getEmailTransactionsByClient(clientId, {
        page,
        limit: 10,
        status: statusFilter && statusFilter !== "all" ? statusFilter : undefined,
        emailType: emailTypeFilter && emailTypeFilter !== "all" ? emailTypeFilter : undefined,
      }),
    enabled: !!clientId,
  });

  const retryMutation = useMutation({
    mutationFn: (id) => retryEmail(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["emailTransactions", type, clientId]);
    },
  });

  const handlePreview = async (transaction) => {
    try {
      const response = await getEmailTransactionById(transaction.id);
      setSelectedTransaction(response.data);
      setPreviewOpen(true);
    } catch (error) {
      console.error("Error fetching transaction details:", error);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading email transactions...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-red-500">
            Error loading email transactions: {error.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  const transactions = data?.data?.data || [];
  const pagination = data?.data?.pagination || { page: 1, totalPages: 1, total: 0 };

  return (
    <>
      <Card>
        <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Mail className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              Email Transactions ({pagination.total})
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={emailTypeFilter} onValueChange={setEmailTypeFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(EMAIL_TYPE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {transactions.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Mail className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p>No email transactions found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sent At</TableHead>
                    <TableHead>Opens</TableHead>
                    <TableHead>Tracking</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((transaction) => {
                    const statusConfig = STATUS_CONFIG[transaction.status] || STATUS_CONFIG.PENDING;
                    const typeConfig = EMAIL_TYPE_CONFIG[transaction.emailType] || EMAIL_TYPE_CONFIG.CUSTOM;
                    const StatusIcon = statusConfig.icon;

                    return (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          <Badge className={typeConfig.color}>{typeConfig.label}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {transaction.subject}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${statusConfig.color} flex items-center gap-1 w-fit`}>
                            <StatusIcon className="h-3 w-3" />
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {transaction.sentAt
                            ? format(new Date(transaction.sentAt), "dd/MM/yyyy HH:mm")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Eye className="h-4 w-4 text-muted-foreground" />
                            <span>{transaction.openCount || 0}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {transaction.trackingCode?.substring(0, 8)}...
                          </code>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePreview(transaction)}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            {transaction.status === "FAILED" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => retryMutation.mutate(transaction.id)}
                                disabled={retryMutation.isPending}
                              >
                                <RefreshCw
                                  className={`h-4 w-4 ${
                                    retryMutation.isPending ? "animate-spin" : ""
                                  }`}
                                />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <div className="text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                      disabled={page === pagination.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Transaction Details
            </DialogTitle>
          </DialogHeader>
          {selectedTransaction && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Recipient</label>
                  <p className="text-sm">{selectedTransaction.recipientEmail}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <Badge className={STATUS_CONFIG[selectedTransaction.status]?.color}>
                    {STATUS_CONFIG[selectedTransaction.status]?.label}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Type</label>
                  <Badge className={EMAIL_TYPE_CONFIG[selectedTransaction.emailType]?.color}>
                    {EMAIL_TYPE_CONFIG[selectedTransaction.emailType]?.label}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Tracking Code</label>
                  <code className="text-xs bg-muted px-2 py-1 rounded block mt-1">
                    {selectedTransaction.trackingCode}
                  </code>
                </div>
              </div>

              <div className="border-t pt-4">
                <label className="text-sm font-medium text-muted-foreground">Subject</label>
                <p className="text-sm mt-1">{selectedTransaction.subject}</p>
              </div>

              <div className="grid grid-cols-3 gap-4 border-t pt-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created</label>
                  <p className="text-sm">
                    {format(new Date(selectedTransaction.createdAt), "dd/MM/yyyy HH:mm")}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Sent</label>
                  <p className="text-sm">
                    {selectedTransaction.sentAt
                      ? format(new Date(selectedTransaction.sentAt), "dd/MM/yyyy HH:mm")
                      : "-"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Opened</label>
                  <p className="text-sm">
                    {selectedTransaction.openedAt
                      ? format(new Date(selectedTransaction.openedAt), "dd/MM/yyyy HH:mm")
                      : "-"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 border-t pt-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Open Count</label>
                  <p className="text-sm flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    {selectedTransaction.openCount || 0} times
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Last Opened</label>
                  <p className="text-sm">
                    {selectedTransaction.lastOpenedAt
                      ? format(new Date(selectedTransaction.lastOpenedAt), "dd/MM/yyyy HH:mm")
                      : "-"}
                  </p>
                </div>
              </div>

              {selectedTransaction.failureReason && (
                <div className="border-t pt-4">
                  <label className="text-sm font-medium text-red-500">Failure Reason</label>
                  <p className="text-sm text-red-600 mt-1 bg-red-50 p-2 rounded">
                    {selectedTransaction.failureReason}
                  </p>
                </div>
              )}

              {selectedTransaction.metadata && (
                <div className="border-t pt-4">
                  <label className="text-sm font-medium text-muted-foreground">Metadata</label>
                  <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-x-auto">
                    {JSON.stringify(selectedTransaction.metadata, null, 2)}
                  </pre>
                </div>
              )}

              {selectedTransaction.ipAddress && (
                <div className="border-t pt-4">
                  <label className="text-sm font-medium text-muted-foreground">Tracking Info</label>
                  <div className="text-sm mt-1 space-y-1">
                    <p>IP: {selectedTransaction.ipAddress}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      User Agent: {selectedTransaction.userAgent}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default EmailTransactionsTab;
